
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useApplyForAffiliate } from '@/hooks/useAffiliate';
import { useIsActiveAffiliate } from '@/hooks/useIsActiveAffiliate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Star, Crown, Shield, Users, TrendingUp, Globe, Zap, Heart, Rocket, ArrowRight, Award, Target, Lightbulb, Sparkles, DollarSign, Trophy, CheckCircle, Bot, Brain, Code, PieChart } from 'lucide-react';
import { TypingEffect } from '@/components/bot-builder/TypingEffect';
import { AnimatedCounter } from '@/components/affiliate/onboarding/AnimatedCounter';
import { IncomeCalculator } from '@/components/affiliate/onboarding/IncomeCalculator';
import { FloatingElements } from '@/components/affiliate/onboarding/FloatingElements';
import { LiveNotifications } from '@/components/affiliate/onboarding/LiveNotifications';

// Form schema
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  website: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal("")),
  socialMedia: z.string().optional(),
  reason: z.string().min(10, { message: "Please provide more details about your vision for the community" }),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: "You must agree to the terms and conditions",
  }),
});

const AffiliateOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { mutate, isPending } = useApplyForAffiliate();
  const { hasAffiliateRecord, isLoading: checkingAffiliate } = useIsActiveAffiliate();

  // Redirect if user already has affiliate record
  useEffect(() => {
    if (!checkingAffiliate && hasAffiliateRecord) {
      navigate('/affiliate');
    }
  }, [hasAffiliateRecord, checkingAffiliate, navigate]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.user_metadata?.name || "",
      email: user?.email || "",
      website: "",
      socialMedia: "",
      reason: "",
      agreeToTerms: false,
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    mutate({
      name: values.name,
      email: values.email,
      reason: values.reason,
      website: values.website,
      social_media: values.socialMedia,
    }, {
      onSuccess: () => {
        toast({
          title: "🎉 Chào mừng bạn đến với cộng đồng Trade Bot 365!",
          description: "Đơn đăng ký của bạn đã được gửi! Chúng tôi sẽ liên hệ sớm để bắt đầu hành trình cùng nhau.",
        });
        navigate('/affiliate');
      },
      onError: (error) => {
        toast({
          title: "Đăng ký thất bại",
          description: "Có lỗi xảy ra khi gửi đơn đăng ký. Vui lòng thử lại.",
          variant: "destructive"
        });
      }
    });
  };

  // Show loading while checking affiliate status
  if (checkingAffiliate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 md:space-y-16 relative overflow-hidden font-inter px-3 md:px-0">
      {/* Floating Elements - Hidden on mobile for performance */}
      <div className="hidden md:block">
        <FloatingElements />
      </div>
      
      {/* Live Notifications - Repositioned for mobile */}
      <div className="hidden sm:block">
        <LiveNotifications />
      </div>

      {/* Mission Badge - Repositioned for mobile */}
      <div className="fixed top-16 sm:top-20 left-2 sm:left-6 z-30 space-y-2 sm:space-y-3">
        <Badge variant="default" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-2 sm:px-4 py-1 sm:py-2 shadow-2xl border border-white/20 text-xs sm:text-sm">
          <Bot className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">TIÊN PHONG AI TRADING</span>
          <span className="sm:hidden">AI TRADING</span>
        </Badge>
        <Badge variant="secondary" className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold px-2 sm:px-4 py-1 sm:py-2 shadow-2xl border border-white/20 text-xs sm:text-sm">
          <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">CỘNG ĐỒNG TRADING</span>
          <span className="sm:hidden">CỘNG ĐỒNG</span>
        </Badge>
      </div>

      {/* Hero Section - Optimized for mobile */}
      <div className="relative text-center space-y-6 md:space-y-10 pt-20 md:pt-16 pb-8 md:pb-16">
        {/* Community Statistics - Mobile responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          <div className="bg-gradient-to-br from-emerald-600/20 to-green-600/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-4 md:p-6 border border-emerald-500/30 hover:scale-105 transition-all duration-500">
            <div className="text-2xl md:text-3xl font-bold text-emerald-400 mb-1 md:mb-2">
              <AnimatedCounter end={2847} suffix="+" />
            </div>
            <div className="text-emerald-300 font-medium text-xs md:text-sm leading-relaxed">Trader trong cộng đồng</div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-4 md:p-6 border border-blue-500/30 hover:scale-105 transition-all duration-500">
            <div className="text-2xl md:text-3xl font-bold text-blue-400 mb-1 md:mb-2">
              <AnimatedCounter end={156} suffix="+" />
            </div>
            <div className="text-blue-300 font-medium text-xs md:text-sm leading-relaxed">Bot được tạo mỗi ngày</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-4 md:p-6 border border-purple-500/30 hover:scale-105 transition-all duration-500 sm:col-span-2 lg:col-span-1">
            <div className="text-2xl md:text-3xl font-bold text-purple-400 mb-1 md:mb-2">
              <AnimatedCounter end={89} suffix="%" />
            </div>
            <div className="text-purple-300 font-medium text-xs md:text-sm leading-relaxed">Trader hài lòng với AI Assistant</div>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          <Badge variant="outline" className="text-sm md:text-base font-semibold px-3 md:px-6 py-2 md:py-3 border-blue-500/50 bg-gradient-to-r from-blue-600/10 to-purple-600/10 backdrop-blur-lg">
            <Globe className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2 text-blue-400" />
            <span className="hidden sm:inline">CÙNG XÂY DỰNG TƯƠNG LAI CỦA AI TRADING</span>
            <span className="sm:hidden">XÂY DỰNG TƯƠNG LAI AI</span>
          </Badge>
          
          <h1 className="leading-tight">
            <div className="bg-gradient-to-r from-blue-400 via-purple-500 to-emerald-500 bg-clip-text text-transparent mb-2 md:mb-3 text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-bold">
              TRADE BOT 365
            </div>
            <div className="text-white mb-2 md:mb-3 text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold">Nền tảng</div>
            <div className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold">
              <TypingEffect 
                text="AI TRADING NO-CODE" 
                speed={100}
                className="inline-block"
              />
            </div>
            <div className="text-sm sm:text-xl md:text-2xl mt-2 md:mt-4 text-gray-300 font-medium leading-relaxed">
              Đưa công nghệ AI đến tay mọi trader Việt Nam
            </div>
          </h1>
          
          <div className="text-base sm:text-xl md:text-2xl font-semibold">
            <TypingEffect 
              text="Bạn có muốn trở thành người tiên phong lan tỏa công nghệ AI Trading?" 
              speed={80}
              className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"
            />
          </div>
          
          <div className="text-sm sm:text-lg md:text-xl text-white max-w-4xl mx-auto leading-relaxed font-medium space-y-2 md:space-y-3">
            <p>
              Tham gia cùng chúng tôi xây dựng <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent font-semibold">cộng đồng trading mạnh nhất Việt Nam</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-4 mt-4 md:mt-6 text-xs sm:text-base">
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                <span>Tạo bot AI không cần code</span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                <span>AI Assistant hỗ trợ 24/7</span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-400 rounded-full flex-shrink-0"></div>
                <span>Kết nối Prop Firm uy tín</span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-pink-400 rounded-full flex-shrink-0"></div>
                <span>Cộng đồng học hỏi mạnh mẽ</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Features Section - Mobile optimized */}
      <div className="relative mb-8 md:mb-16">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-emerald-600/10 rounded-2xl md:rounded-3xl blur-xl"></div>
        <div className="relative bg-white/5 backdrop-blur-xl border border-blue-500/30 rounded-2xl md:rounded-3xl p-6 md:p-12 shadow-2xl">
          <div className="text-center space-y-6 md:space-y-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              TẠI SAO <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">TRADE BOT 365</span> 
              <span className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent"> KHÁC BIỆT?</span>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 text-left">
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <Code className="w-6 h-6 md:w-7 md:h-7 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2">Nền tảng No-Code tiên tiến</h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed">Tạo bot giao dịch AI phức tạp chỉ bằng drag & drop, không cần kiến thức lập trình.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 md:space-x-4">
                  <Brain className="w-6 h-6 md:w-7 md:h-7 text-purple-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2">AI Assistant thông minh</h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed">Trợ lý AI hiểu sâu về thị trường, tư vấn chiến lược và hỗ trợ 24/7.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <PieChart className="w-6 h-6 md:w-7 md:h-7 text-emerald-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2">Kết nối Prop Firm uy tín</h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed">Đối tác với các quỹ Prop Firm hàng đầu, cung cấp vốn giao dịch từ $10K đến $200K.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 md:space-x-4">
                  <Users className="w-6 h-6 md:w-7 md:h-7 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2">Cộng đồng học hỏi</h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed">Chia sẻ kiến thức và hỗ trợ lẫn nhau trong cộng đồng trader Việt Nam.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Vision Section - Mobile responsive */}
      <div className="mb-8 md:mb-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent mb-2 md:mb-4">
            SỨ MỆNH & TẦM NHÌN CỦA CHÚNG TÔI
          </h2>
          <p className="text-sm md:text-lg text-gray-300">Dân chủ hóa công nghệ AI Trading cho mọi người</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white">Sứ Mệnh</h3>
              <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
                Đưa công nghệ AI Trading đến tay mọi trader Việt Nam, loại bỏ rào cản kỹ thuật và tài chính
              </p>
            </div>
          </div>

          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-blue-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                <Lightbulb className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white">Tầm Nhìn</h3>
              <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
                Trở thành nền tảng AI Trading số 1 Đông Nam Á, nơi mọi trader đều có thể thành công
              </p>
            </div>
          </div>

          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                <Heart className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white">Giá Trị Cốt Lõi</h3>
              <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
                Minh bạch, cộng đồng, đổi mới và cam kết mang lại giá trị thực cho mọi trader
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Stories Section - Mobile responsive */}
      <div className="mb-8 md:mb-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent mb-2 md:mb-4">
            CÂU CHUYỆN TỪ CỘNG ĐỒNG
          </h2>
          <p className="text-sm md:text-lg text-gray-300">Những người đã góp phần xây dựng cộng đồng</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 hover:scale-105 transition-all duration-500 shadow-xl">
              <div className="flex items-center justify-center mb-4 md:mb-6">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <blockquote className="text-base md:text-lg font-medium text-center text-white mb-4 md:mb-6 leading-relaxed">
                "Tôi đã giúp hơn 200 trader trong cộng đồng học cách sử dụng AI Trading. Việc thấy họ từ không biết gì đến tạo được bot thành công là niềm vui lớn nhất!"
              </blockquote>
              <div className="text-center">
                <cite className="text-sm md:text-base font-semibold text-emerald-400">— Anh Minh Hoàng, Hà Nội</cite>
                <div className="text-xs md:text-sm text-gray-400 mt-1">Community Leader</div>
                <div className="flex items-center justify-center mt-2 md:mt-3">
                  <Users className="w-3 h-3 md:w-4 md:h-4 text-emerald-400 mr-1 md:mr-2" />
                  <span className="text-emerald-400 font-medium text-xs md:text-sm">200+ trader đã hỗ trợ</span>
                </div>
              </div>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 hover:scale-105 transition-all duration-500 shadow-xl">
              <div className="flex items-center justify-center mb-4 md:mb-6">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <blockquote className="text-base md:text-lg font-medium text-center text-white mb-4 md:mb-6 leading-relaxed">
                "Từ khi tham gia Trade Bot 365, tôi không chỉ cải thiện kỹ năng trading mà còn học được cách chia sẻ kiến thức. Cộng đồng này thật sự đáng giá!"
              </blockquote>
              <div className="text-center">
                <cite className="text-sm md:text-base font-semibold text-purple-400">— Chị Thu Hương, TP.HCM</cite>
                <div className="text-xs md:text-sm text-gray-400 mt-1">AI Bot Creator</div>
                <div className="flex items-center justify-center mt-2 md:mt-3">
                  <Bot className="w-3 h-3 md:w-4 md:h-4 text-purple-400 mr-1 md:mr-2" />
                  <span className="text-purple-400 font-medium text-xs md:text-sm">50+ bot đã tạo ra</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Income Calculator Section */}
      <div className="mb-8 md:mb-16">
        <IncomeCalculator />
      </div>

      {/* Benefits Sections - Mobile responsive */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-16">
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-blue-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
          <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white">Phát Triển Bản Thân</h3>
            <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
              Nâng cao kiến thức về AI Trading, học hỏi từ cộng đồng và phát triển kỹ năng lãnh đạo
            </p>
            <div className="bg-emerald-500/20 rounded-xl p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-emerald-400">∞</div>
              <div className="text-emerald-300 text-xs md:text-sm">Cơ hội học hỏi</div>
            </div>
          </div>
        </div>

        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
          <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <Crown className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white">Uy Tín Trong Cộng Đồng</h3>
            <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
              Trở thành người tiên phong được tôn trọng trong cộng đồng AI Trading Việt Nam
            </p>
            <div className="bg-purple-500/20 rounded-xl p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-purple-400">VIP</div>
              <div className="text-purple-300 text-xs md:text-sm">Thành viên đặc biệt</div>
            </div>
          </div>
        </div>

        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/30 to-orange-500/30 rounded-xl md:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
          <div className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8 text-center space-y-4 md:space-y-6 hover:scale-105 transition-all duration-500 shadow-xl">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <Rocket className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white">Tiếp Cận Sớm</h3>
            <p className="text-gray-300 leading-relaxed text-xs md:text-sm">
              Trải nghiệm các tính năng mới nhất và có cơ hội góp ý định hướng phát triển sản phẩm
            </p>
            <div className="bg-amber-500/20 rounded-xl p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-amber-400">BETA</div>
              <div className="text-amber-300 text-xs md:text-sm">Ưu tiên trải nghiệm</div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Form - Mobile optimized */}
      <Card className="max-w-4xl mx-auto shadow-2xl border-0 bg-white/5 backdrop-blur-xl relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10"></div>
        <div className="absolute top-0 right-0 w-32 h-32 md:w-40 md:h-40 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 md:w-40 md:h-40 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-2xl"></div>
        
        <CardHeader className="text-center relative z-10 pb-6 md:pb-8">
          <div className="flex justify-center mb-4 md:mb-6">
            <Badge variant="secondary" className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 text-white border border-white/20 px-3 md:px-6 py-2 md:py-3 text-sm md:text-lg font-semibold">
              <Users className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
              <span className="hidden sm:inline">THAM GIA CỘNG ĐỒNG TIÊN PHONG</span>
              <span className="sm:hidden">THAM GIA CỘNG ĐỒNG</span>
            </Badge>
          </div>
          <CardTitle className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-emerald-500 bg-clip-text text-transparent">
            HÃY CHIA SẺ TẦM NHÌN CỦA BẠN
          </CardTitle>
          <CardDescription className="text-sm sm:text-base md:text-lg lg:text-xl mt-4 md:mt-6 max-w-2xl mx-auto leading-relaxed font-medium text-white">
            Chúng tôi muốn hiểu về đam mê và tầm nhìn của bạn trong việc 
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"> xây dựng cộng đồng AI Trading mạnh mẽ.</span>
          </CardDescription>
        </CardHeader>
        
        <CardContent className="relative z-10 px-4 md:px-8 lg:px-12">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 md:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm md:text-base font-semibold flex items-center text-white">
                        <Users className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 text-blue-400" />
                        Họ và Tên *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Tên đầy đủ của bạn" 
                          className="bg-white/10 backdrop-blur-sm border border-white/20 focus:border-blue-500/50 focus:ring-blue-500/30 h-11 md:h-12 text-sm md:text-base font-medium transition-all duration-300 hover:border-white/30"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm md:text-base font-semibold text-white">Địa chỉ Email *</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="email@domain.com" 
                          className="bg-white/10 backdrop-blur-sm border border-white/20 focus:border-blue-500/50 focus:ring-blue-500/30 h-11 md:h-12 text-sm md:text-base font-medium transition-all duration-300 hover:border-white/30"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm md:text-base font-semibold text-white">Website (Tùy chọn)</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="https://website-cua-ban.com" 
                          className="bg-white/10 backdrop-blur-sm border border-white/20 focus:border-blue-500/50 focus:ring-blue-500/30 h-11 md:h-12 text-sm md:text-base font-medium transition-all duration-300 hover:border-white/30"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="socialMedia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm md:text-base font-semibold text-white">Mạng xã hội (Tùy chọn)</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="@tentaikhoan hoặc link" 
                          className="bg-white/10 backdrop-blur-sm border border-white/20 focus:border-blue-500/50 focus:ring-blue-500/30 h-11 md:h-12 text-sm md:text-base font-medium transition-all duration-300 hover:border-white/30"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm md:text-base font-semibold text-white">Tầm nhìn của bạn cho cộng đồng AI Trading? *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Chia sẻ về đam mê trading, kinh nghiệm của bạn và cách bạn muốn đóng góp để xây dựng cộng đồng AI Trading mạnh mẽ tại Việt Nam. Bạn có ý tưởng gì để giúp các trader khác tiếp cận công nghệ AI dễ dàng hơn?" 
                        className="min-h-[100px] md:min-h-[120px] bg-white/10 backdrop-blur-sm border border-white/20 focus:border-blue-500/50 focus:ring-blue-500/30 text-sm md:text-base resize-none transition-all duration-300 hover:border-white/30"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription className="text-xs md:text-sm text-blue-300 font-medium">
                      Hãy chia sẻ tầm nhìn và đam mê của bạn. Chúng tôi muốn hiểu cách bạn có thể đóng góp cho cộng đồng.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="agreeToTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 md:space-x-4 space-y-0 p-4 md:p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-xl md:rounded-2xl border border-white/20">
                    <FormControl>
                      <Checkbox 
                        checked={field.value} 
                        onCheckedChange={field.onChange}
                        className="mt-1 w-4 h-4 md:w-5 md:h-5"
                      />
                    </FormControl>
                    <div className="space-y-1 md:space-y-2 leading-relaxed">
                      <FormLabel className="text-sm md:text-base font-semibold flex items-center text-white cursor-pointer">
                        <Shield className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 text-blue-400" />
                        Tôi đồng ý tham gia cộng đồng Trade Bot 365! *
                      </FormLabel>
                      <FormDescription className="text-xs md:text-sm text-gray-300">
                        Bằng cách chọn ô này, bạn đồng ý với Điều khoản Đối tác và Chính sách Bảo mật của chúng tôi.
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-4 md:space-y-6 pt-6 md:pt-8 pb-8 md:pb-12 relative z-10">
          <div className="flex flex-col sm:flex-row justify-between items-center w-full space-y-3 sm:space-y-0 gap-3">
            <Button variant="outline" onClick={() => navigate('/')} className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 text-sm md:text-base border border-white/20 bg-white/5 hover:bg-white/10 min-h-[44px]">
              Quay lại Dashboard
            </Button>
            <Button 
              type="submit" 
              onClick={form.handleSubmit(onSubmit)} 
              disabled={isPending}
              className="w-full sm:w-auto px-8 md:px-12 py-3 md:py-4 text-base md:text-lg font-semibold bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 hover:opacity-90 text-white shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 min-h-[44px]"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin mr-2 md:mr-3" />
                  <span className="hidden sm:inline">Đang gửi đơn...</span>
                  <span className="sm:hidden">Đang gửi...</span>
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                  <span className="hidden sm:inline">THAM GIA CỘNG ĐỒNG</span>
                  <span className="sm:hidden">THAM GIA</span>
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 md:ml-3" />
                </>
              )}
            </Button>
          </div>
          <div className="text-center max-w-2xl">
            <p className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">
              🚀 <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Hành trình xây dựng cộng đồng AI Trading</span> bắt đầu từ đây. 
            </p>
            <p className="text-sm md:text-base text-gray-300">
              Chào mừng đến với tương lai của giao dịch. Chào mừng đến với <span className="font-semibold text-blue-400">Trade Bot 365</span>.
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AffiliateOnboarding;
